import React, { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Search,
  CircleHelp,
  Shield,
  CreditCard,
  Users,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  Headphones,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  const faqSections = [
    {
      key: 'general',
      title: 'General Questions',
      icon: CircleHelp,
      headerClass: 'bg-purple-100',
      iconWrapClass: 'bg-white/70 text-purple-600',
      items: [
        {
          question: 'What is KoPartner?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                KoPartner is India&apos;s #1 Professional Social Support Services Platform operated by
                SET INDIA BUSINESS PVT LTD. With millions of registered KoPartners across ALL Indian
                pin codes, we connect individuals who need companionship and assistance with verified,
                professional KoPartners for services like Elder Care, Hangingout, Movie Partners,
                Shopping Buddies, Medical Support, Clubbing, Travel Partners, and more.
              </p>
              <p className="text-gray-700 leading-8">
                IMPORTANT: KoPartner is NOT a dating service, matrimonial platform, or escort
                service. All interactions are strictly professional and consent-first.
              </p>
            </>
          ),
        },
        {
          question: 'Is KoPartner a dating app or escort service?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                ABSOLUTELY NOT. KoPartner is a professional social support services platform ONLY.
                All services are strictly professional and non-romantic. Any romantic, sexual, or
                dating activities are STRICTLY PROHIBITED and will result in immediate account
                termination without refund. We maintain zero-tolerance for any violation of
                professional boundaries.
              </p>
              <p className="text-gray-700 leading-8">
                KoPartner operates as a legitimate technology intermediary under the Information
                Technology Act, 2000.
              </p>
            </>
          ),
        },
        {
          question: 'Who can use KoPartner?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                KoPartner is available to all individuals above 18 years of age residing in India.
                Users must have a valid Indian mobile number and agree to our Terms of Service,
                Privacy Policy, and Code of Conduct.
              </p>
              <p className="text-gray-700 leading-8">As a Client: Book professional social support services</p>
              <p className="text-gray-700 leading-8">
                As a KoPartner: Register to provide services and earn (membership required)
              </p>
            </>
          ),
        },
        {
          question: 'Is KoPartner available in my area?',
          answer: (
            <p className="text-gray-700 leading-8">
              YES! KoPartner operates across ALL of India - every state, every district, and every
              pin code. With millions of registered KoPartners nationwide, we have comprehensive
              coverage from metropolitan cities to small towns. Simply enter your pin code to find
              available KoPartners in your area.
            </p>
          ),
        },
      ],
    },
    {
      key: 'safety',
      title: 'Safety & Security',
      icon: Shield,
      headerClass: 'bg-green-100',
      iconWrapClass: 'bg-white/70 text-green-600',
      items: [
        {
          question: 'Is KoPartner safe to use?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Safety is our highest priority. We implement multiple security measures:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• AI-powered profile verification</li>
                <li>• Government ID verification for KoPartners</li>
                <li>• SOS emergency button in app</li>
                <li>• 24/7 safety monitoring team</li>
                <li>• Secure payment verification (no card data stored)</li>
                <li>• User ratings and reviews</li>
                <li>• Strict Code of Conduct enforcement</li>
                <li>• Confidential reporting system</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                We comply with IT Act, 2000 and IT (Reasonable Security Practices) Rules, 2011
                for data protection.
              </p>
            </>
          ),
        },
        {
          question: 'How are KoPartners verified?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Every KoPartner undergoes a comprehensive verification process:
              </p>
              <ol className="space-y-2 text-gray-700 leading-8">
                <li>1. Mobile number OTP verification</li>
                <li>2. Profile photo AI verification</li>
                <li>3. Government ID verification (Aadhaar/PAN)</li>
                <li>4. Background information review</li>
                <li>5. Terms &amp; Code of Conduct acceptance</li>
              </ol>
              <p className="mt-4 text-gray-700 leading-8">
                Verified profiles are marked with a verification badge. We recommend only booking
                verified KoPartners.
              </p>
            </>
          ),
        },
        {
          question: 'What is the SOS feature?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                The SOS feature is an emergency safety button available in the KoPartner app.
                When activated:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• Immediately alerts our 24/7 safety team</li>
                <li>• Shares your real-time location</li>
                <li>• Notifies your emergency contacts</li>
                <li>• Triggers immediate response protocol</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                Both Clients and KoPartners have access to this feature for any safety concerns.
              </p>
            </>
          ),
        },
        {
          question: 'What if I face misconduct or harassment?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Report immediately through these channels:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• In-app Report feature</li>
                <li>• Email: safety@kopartner.in</li>
                <li>• Grievance: grievance@kopartner.in</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                We investigate all reports within 24 hours. Depending on severity, actions include
                warnings, suspension, permanent bans, and reporting to law enforcement. All reports
                are handled confidentially.
              </p>
              <p className="mt-4 text-gray-700 leading-8">
                We maintain zero-tolerance for misconduct.
              </p>
            </>
          ),
        },
      ],
    },
    {
      key: 'payments',
      title: 'Payments & Pricing',
      icon: CreditCard,
      headerClass: 'bg-blue-100',
      iconWrapClass: 'bg-white/70 text-blue-600',
      items: [
        {
          question: 'What are the service charges?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Our transparent pricing (subject to 18% GST):
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• Elder Care: ₹1,000/hour</li>
                <li>• Hangingout: ₹1,500/hour</li>
                <li>• Clubbing: ₹2,000/hour</li>
                <li>• Movie Partner: ₹2,000/hour</li>
                <li>• Shopping Buddy: ₹2,000/hour</li>
                <li>• Medical Support: ₹2,000/hour</li>
                <li>• Travel Partner: ₹2,000/hour</li>
                <li>• Domestic Help: Varies</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                All prices are displayed upfront before booking.
              </p>
            </>
          ),
        },
        {
          question: 'How do I make payments?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Payments are made via PhonePe QR code:
              </p>
              <ol className="space-y-2 text-gray-700 leading-8">
                <li>1. Select your services and duration</li>
                <li>2. Scan the displayed QR code using any UPI app</li>
                <li>3. Pay to: SET INDIA BUSINESS PVT LTD</li>
                <li>4. Upload payment screenshot</li>
                <li>5. Our AI verifies the payment automatically</li>
              </ol>
              <p className="mt-4 text-gray-700 leading-8">
                IMPORTANT: We do NOT store any payment card details, bank account information,
                or UPI credentials.
              </p>
            </>
          ),
        },
        {
          question: 'Does KoPartner store my payment details?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                NO. KoPartner does NOT collect, store, process, or retain:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• Credit/Debit card numbers</li>
                <li>• CVV or card security codes</li>
                <li>• Bank account details</li>
                <li>• UPI PIN or credentials</li>
                <li>• Net banking passwords</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                All payments are direct UPI transfers to our verified business account. This protects
                you from any potential cyber fraud.
              </p>
            </>
          ),
        },
        {
          question: 'What are the KoPartner membership plans?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                To become a registered KoPartner and start earning:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• 6 Months: ₹199 + ₹36 GST = ₹235</li>
                <li>• 1 Year: ₹499 + ₹90 GST = ₹589 (MOST POPULAR)</li>
                <li>• Lifetime: ₹999 + ₹180 GST = ₹1,179</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">Current Offer: Up to 60% OFF!</p>
              <p className="mt-4 text-gray-700 leading-8">
                IMPORTANT: Membership fees are STRICTLY NON-REFUNDABLE under any circumstances.
              </p>
            </>
          ),
        },
        {
          question: 'What is the refund policy?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                MEMBERSHIP: Strictly NON-REFUNDABLE under any circumstances.
              </p>
              <p className="mb-2 text-gray-700 leading-8">SERVICE BOOKINGS:</p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• Cancel 24+ hours before: 100% refund to KoPartner Wallet</li>
                <li>• Cancel 12-24 hours before: 50% refund to KoPartner Wallet</li>
                <li>• Cancel &lt;12 hours or no-show: No refund</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                IMPORTANT: ALL refunds are credited to your KoPartner Wallet ONLY for use on future
                bookings. NO cash refunds, bank transfers, or UPI refunds will be processed.
              </p>
            </>
          ),
        },
        {
          question: 'What is KoPartner Wallet?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                KoPartner Wallet is your in-app credit balance for refunds and future bookings:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• Receives service refund credits</li>
                <li>• Can ONLY be used for bookings on KoPartner</li>
                <li>• NON-transferable to other users</li>
                <li>• NON-withdrawable to bank/UPI</li>
                <li>• No expiry (while account active)</li>
                <li>• Forfeited on account termination</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                Wallet credits have no cash value.
              </p>
            </>
          ),
        },
      ],
    },
    {
      key: 'partners',
      title: 'For KoPartners',
      icon: Users,
      headerClass: 'bg-pink-100',
      iconWrapClass: 'bg-white/70 text-pink-600',
      items: [
        {
          question: 'How much can I earn as a KoPartner?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                KoPartners can earn attractive income depending on services, duration, and booking
                frequency. Typical service rates range from ₹1,000/hour to ₹2,000/hour.
              </p>
              <p className="text-gray-700 leading-8">
                Your actual earnings depend on your availability, profile quality, service category,
                ratings, and location demand.
              </p>
            </>
          ),
        },
        {
          question: 'How do I become a KoPartner?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Simple 5-step process:
              </p>
              <ol className="space-y-2 text-gray-700 leading-8">
                <li>1. Visit kopartner.in → Click 'Become a KoPartner'</li>
                <li>2. Register with your mobile number (OTP verification)</li>
                <li>3. Complete your profile (photo, bio, services, rates)</li>
                <li>4. Pay membership fee (from ₹199 only)</li>
                <li>5. Upload payment screenshot for AI verification</li>
              </ol>
              <p className="mt-4 text-gray-700 leading-8">
                Once verified, your profile goes live and you can start receiving bookings!
              </p>
            </>
          ),
        },
        {
          question: 'How do I receive my earnings?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                Earnings are transferred directly to your registered bank account:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• Automatic processing after each completed booking</li>
                <li>• 80% of service fee is your share</li>
                <li>• Bank transfer within 24-48 hours</li>
                <li>• Track all earnings in your dashboard</li>
                <li>• Monthly statements available</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                Ensure your bank details are accurate in your profile.
              </p>
            </>
          ),
        },
        {
          question: 'Can I set my own service rates?',
          answer: (
            <>
              <p className="mb-4 text-gray-700 leading-8">
                While we have base rates for consistency, experienced KoPartners have flexibility:
              </p>
              <ul className="space-y-2 text-gray-700 leading-8">
                <li>• New KoPartners: Follow base rates</li>
                <li>• Premium KoPartners (high ratings, experience): Can set higher rates</li>
                <li>• Custom services: Pricing flexibility within guidelines</li>
              </ul>
              <p className="mt-4 text-gray-700 leading-8">
                Final pricing is always transparent to Clients before booking.
              </p>
            </>
          ),
        },
        {
          question: 'Do I get guaranteed bookings?',
          answer: (
            <p className="text-gray-700 leading-8">
              No, bookings are not guaranteed. Your success as a KoPartner depends on:
              <br /><br />
              • Profile quality and completeness
              <br />
              • Service ratings and reviews
              <br />
              • Competitive pricing
              <br />
              • Availability and response time
              <br />
              • Location and service demand
              <br /><br />
              We recommend maintaining high ratings and positive reviews to attract more bookings.
            </p>
          ),
        },
      ],
    },
  ];

  // Enhanced search functionality - safely handles both string and JSX answers
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return faqSections;
    
    const query = searchQuery.toLowerCase();
    return faqSections
      .map(section => {
        const filteredItems = section.items.filter(item => {
          if (item.question.toLowerCase().includes(query)) return true;
          if (typeof item.answer === 'string') {
            return item.answer.toLowerCase().includes(query);
          }
          return false;
        });
        
        return {
          ...section,
          items: filteredItems
        };
      })
      .filter(section => section.items.length > 0);
  }, [searchQuery, faqSections]);

  const toggleItem = (sectionKey, index) => {
    const itemKey = `${sectionKey}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [itemKey]: prev[itemKey] ? null : true
    }));
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const isItemOpen = (sectionKey, index) => {
    const itemKey = `${sectionKey}-${index}`;
    return !!openItems[itemKey];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-4xl mx-auto px-4 py-12 pt-24">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-600 text-lg">
            Find answers to common questions about KoPartner
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-12 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm"
          />
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              type="button"
            >
              ✕
            </button>
          )}
        </div>

        {/* Statistics/Features Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
            <div className="flex justify-center mb-2">
              <Users className="text-purple-600" size={28} />
            </div>
            <div className="font-bold text-purple-700 text-lg">Millions of</div>
            <div className="text-purple-600 font-semibold">KoPartners</div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
            <div className="flex justify-center mb-2">
              <MapPin className="text-blue-600" size={28} />
            </div>
            <div className="font-bold text-blue-700 text-lg">All India</div>
            <div className="text-blue-600 font-semibold">Pin Codes</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
            <div className="flex justify-center mb-2">
              <CheckCircle className="text-green-600" size={28} />
            </div>
            <div className="font-bold text-green-700 text-lg">AI-Verified</div>
            <div className="text-green-600 font-semibold">Profiles</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 text-center border border-orange-200">
            <div className="flex justify-center mb-2">
              <Headphones className="text-orange-600" size={28} />
            </div>
            <div className="font-bold text-orange-700 text-lg">24/7</div>
            <div className="text-orange-600 font-semibold">Support</div>
          </div>
        </div>

        {filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No FAQs found matching your search.</p>
            <button
              onClick={clearSearch}
              className="mt-4 text-purple-600 font-semibold hover:underline"
              type="button"
            >
              Clear search
            </button>
          </div>
        ) : (
          filteredSections.map((section) => (
            <div key={section.key} className="mb-8">
              <div className={`${section.headerClass} rounded-xl p-4 mb-4 flex items-center gap-3`}>
                <div className={`p-2 rounded-full ${section.iconWrapClass}`}>
                  <section.icon size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">{section.title}</h2>
                <span className="ml-auto text-sm text-gray-500">{section.items.length} questions</span>
              </div>

              <div className="space-y-3">
                {section.items.map((item, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                    <button
                      onClick={() => toggleItem(section.key, index)}
                      className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition text-left"
                      type="button"
                    >
                      <span className="font-semibold text-gray-800">{item.question}</span>
                      {isItemOpen(section.key, index) ? (
                        <ChevronUp size={20} className="text-purple-600 flex-shrink-0 ml-4" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0 ml-4" />
                      )}
                    </button>
                    {isItemOpen(section.key, index) && (
                      <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                        {item.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Booking & Services Section */}
        <div className="mt-8 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-orange-100 p-4">
            <h2 className="text-xl font-bold text-gray-800">Booking & Services</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              {
                question: 'How do I book a KoPartner?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8">Easy booking process:</p>
                    <ol className="space-y-2 text-gray-700 leading-8">
                      <li>1. Sign up/Login with your mobile number</li>
                      <li>2. Enter your pin code (we cover all India)</li>
                      <li>3. Select your required services and duration</li>
                      <li>4. Pay via QR code (PhonePe/UPI)</li>
                      <li>5. Upload payment screenshot</li>
                      <li>6. AI verifies → Browse available KoPartners</li>
                      <li>7. Select and confirm your booking</li>
                    </ol>
                    <p className="mt-4 text-gray-700 leading-8">
                      You'll receive confirmation with KoPartner details.
                    </p>
                  </>
                )
              },
              {
                question: 'How do I choose a KoPartner?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8 font-bold">
                      IMPORTANT: The selection of a KoPartner is ENTIRELY YOUR RESPONSIBILITY based on:
                    </p>
                    <ul className="space-y-2 text-gray-700 leading-8">
                      <li>• Your personal preference and requirements</li>
                      <li>• Profile information and photos</li>
                      <li>• Ratings and reviews from other users</li>
                      <li>• Service offerings and availability</li>
                      <li>• Location proximity</li>
                    </ul>
                    <p className="mt-4 text-gray-700 leading-8">
                      We do NOT influence or control your choice in any manner. KoPartner is not 
                      responsible for your selection or satisfaction with the chosen KoPartner.
                    </p>
                  </>
                )
              },
              {
                question: 'Can I cancel a booking?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8">
                      Yes, you can cancel through 'My Bookings':
                    </p>
                    <ul className="space-y-2 text-gray-700 leading-8">
                      <li>• 24+ hours before: 100% refund to Wallet</li>
                      <li>• 12-24 hours before: 50% refund to Wallet</li>
                      <li>• &lt;12 hours before: No refund</li>
                      <li>• No-show: No refund</li>
                    </ul>
                    <p className="mt-4 text-gray-700 leading-8">
                      KoPartner cancellation: 100% refund to your Wallet
                    </p>
                  </>
                )
              },
              {
                question: 'What if the KoPartner doesn\'t show up?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8">
                      In case of KoPartner no-show:
                    </p>
                    <ol className="space-y-2 text-gray-700 leading-8">
                      <li>1. Report immediately through the app</li>
                      <li>2. Our team investigates within 24 hours</li>
                      <li>3. 100% refund credited to your Wallet</li>
                      <li>4. KoPartner faces penalties</li>
                    </ol>
                    <p className="mt-4 text-gray-700 leading-8">
                      Always use platform features to document and report issues.
                    </p>
                  </>
                )
              }
            ].map((item, index) => {
              const itemKey = `booking-${index}`;
              const isOpen = !!openItems[itemKey];
              return (
                <div key={index}>
                  <button
                    onClick={() => setOpenItems(prev => ({ ...prev, [itemKey]: isOpen ? null : true }))}
                    className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition text-left"
                    type="button"
                  >
                    <span className="font-semibold text-gray-800">{item.question}</span>
                    {isOpen ? (
                      <ChevronUp size={20} className="text-purple-600 flex-shrink-0 ml-4" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400 flex-shrink-0 ml-4" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legal & Compliance Section */}
        <div className="mt-8 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="bg-red-100 p-4">
            <h2 className="text-xl font-bold text-gray-800">Legal & Compliance</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              {
                question: 'Is KoPartner legal?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8">
                      Yes, KoPartner is a fully legal platform operating as a registered intermediary 
                      under Indian law:
                    </p>
                    <ul className="space-y-2 text-gray-700 leading-8">
                      <li>• Operated by SET INDIA BUSINESS PVT LTD (registered company)</li>
                      <li>• Compliant with Information Technology Act, 2000</li>
                      <li>• Follows IT (Intermediary Guidelines) Rules, 2021</li>
                      <li>• Adheres to Consumer Protection Act, 2019</li>
                      <li>• All services are professional and legal</li>
                    </ul>
                    <p className="mt-4 text-gray-700 leading-8">
                      We are NOT a dating, escort, or adult services platform.
                    </p>
                  </>
                )
              },
              {
                question: 'Can I claim cyber fraud against KoPartner?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8 font-bold">
                      NO. KoPartner does not collect, store, or process any financial information:
                    </p>
                    <ul className="space-y-2 text-gray-700 leading-8">
                      <li>• No card details stored</li>
                      <li>• No bank credentials stored</li>
                      <li>• No UPI PINs captured</li>
                      <li>• All payments are voluntary UPI transfers</li>
                    </ul>
                    <p className="mt-4 text-gray-700 leading-8">
                      Since we don't handle financial data, cyber fraud claims are not applicable. 
                      All payments are your voluntary choice made through your own banking app.
                    </p>
                  </>
                )
              },
              {
                question: 'What is KoPartner\'s liability?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8">
                      As an intermediary under IT Act, 2000 (Section 79), KoPartner:
                    </p>
                    <ul className="space-y-2 text-gray-700 leading-8">
                      <li>• Is NOT liable for User-to-User disputes</li>
                      <li>• Is NOT responsible for KoPartner conduct</li>
                      <li>• Does NOT guarantee service quality</li>
                      <li>• Maximum liability limited to ₹1,000</li>
                    </ul>
                    <p className="mt-4 text-gray-700 leading-8">
                      Users assume responsibility for their selections and interactions. Read our 
                      full Terms for complete liability details.
                    </p>
                  </>
                )
              },
              {
                question: 'How do I contact support?',
                answer: (
                  <>
                    <p className="mb-4 text-gray-700 leading-8">
                      Multiple support channels:
                    </p>
                    <ul className="space-y-2 text-gray-700 leading-8">
                      <li>• Email: support@kopartner.in</li>
                      <li>• Safety Issues: safety@kopartner.in</li>
                      <li>• Grievance: grievance@kopartner.in</li>
                      <li>• Legal: legal@kopartner.in</li>
                    </ul>
                    <p className="mt-4 text-gray-700 leading-8">
                      In-app support also available. Response within 24-48 hours.
                    </p>
                  </>
                )
              }
            ].map((item, index) => {
              const itemKey = `legal-${index}`;
              const isOpen = !!openItems[itemKey];
              return (
                <div key={index}>
                  <button
                    onClick={() => setOpenItems(prev => ({ ...prev, [itemKey]: isOpen ? null : true }))}
                    className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition text-left"
                    type="button"
                  >
                    <span className="font-semibold text-gray-800">{item.question}</span>
                    {isOpen ? (
                      <ChevronUp size={20} className="text-purple-600 flex-shrink-0 ml-4" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400 flex-shrink-0 ml-4" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                      {item.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Important Disclaimer */}
        <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
          <h3 className="font-bold text-yellow-800 mb-2">⚠️ Important Disclaimer</h3>
          <p className="text-gray-700 leading-8">
            The information in these FAQs is for general guidance only and does not constitute legal 
            advice. For complete terms, please read our Terms and Conditions, Privacy Policy, Refund 
            Policy, and Code of Conduct. In case of any conflict, the formal policy documents shall 
            prevail.
          </p>
        </div>

        {/* Contact Support Section */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
          <p className="mb-6">Our support team is available 24/7 to help</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:help@kopartner.in" 
              className="flex items-center justify-center space-x-2 bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
            >
              <Mail size={20} />
              <span>Contact Support</span>
            </a>
            {/* <a 
              href="tel:8796371910" 
              className="flex items-center justify-center space-x-2 bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition"
            >
              <Phone size={20} />
              <span>8796371910</span>
            </a> */}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQPage;
